import React, { useMemo, useState } from 'react';
import { Calendar as CalendarIcon, List, ChevronLeft, ChevronRight, Moon, Sun, AlertCircle, Info, Star, ArrowRight, Calendar } from 'lucide-react';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Tabs } from '../components/ui/Tabs';
import { initialAstroEvents, initialForecasts, ZODIAC_SIGNS, formatDateRu, getEventTypeLabel } from '../data/forecastCmsData';
interface CalendarPageProps {
  onNavigate: (page: string) => void;
}
// Helper to format month and year
const formatMonthYear = (date: Date) => {
  const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
};
// Generate calendar grid days
const getDaysInMonth = (year: number, month: number) => {
  const date = new Date(year, month, 1);
  const days = [];
  // Get day of week of the 1st (0 = Sunday, 1 = Monday, etc.)
  // Adjust so Monday is 0, Sunday is 6
  let firstDay = date.getDay() - 1;
  if (firstDay === -1) firstDay = 6;
  // Add empty slots for days before the 1st
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  // Add days of the month
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};
export function CalendarPage({
  onNavigate
}: CalendarPageProps) {
  const [view, setView] = useState<'month' | 'list'>('list');
  const [mainTab, setMainTab] = useState('calendar');
  const [selectedSign, setSelectedSign] = useState('aries');
  const [forecastPeriod, setForecastPeriod] = useState('day');
  // Use current date for forecasts, but keep the 2026 date for the calendar demo
  const [currentDate, setCurrentDate] = useState(new Date(2026, 2, 1)); // March 2026 matches our mock data
  const [activeTooltipDay, setActiveTooltipDay] = useState<number | null>(null);
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };
  // Current transits mock data
  const currentTransits = [{
    planet: '☉ Солнце',
    sign: '♓ Рыбы',
    degree: "10°13'",
    element: 'water'
  }, {
    planet: '☽ Луна',
    sign: '♒ Водолей',
    degree: "6°40'",
    element: 'air'
  }, {
    planet: '☿ Меркурий',
    sign: '♓ Рыбы',
    degree: "22°02'",
    element: 'water'
  }, {
    planet: '♀ Венера',
    sign: '♈ Овен',
    degree: "22°59'",
    element: 'fire'
  }, {
    planet: '♂ Марс',
    sign: '♋ Рак',
    degree: "28°36'",
    element: 'water'
  }, {
    planet: '♃ Юпитер',
    sign: '♋ Рак',
    degree: "15°15'",
    element: 'water'
  }, {
    planet: '♄ Сатурн',
    sign: '♈ Овен',
    degree: "1°42'",
    element: 'fire'
  }];
  const elementColors: Record<string, string> = {
    fire: 'text-red-600',
    earth: 'text-emerald-600',
    air: 'text-indigo-600',
    water: 'text-sky-600' // Fixed from amber to sky
  };
  // Use CMS data instead of hardcoded allEvents
  const allEvents = useMemo(() => {
    return initialAstroEvents.filter(e => e.status === 'published' || e.status === 'archived').map(e => ({
      id: e.id,
      dateStr: e.date_start,
      displayDate: formatDateRu(e.date_start),
      type: getEventTypeLabel(e.event_type),
      title: e.title,
      desc: e.description_short,
      icon: e.event_type === 'eclipse' || e.event_type === 'ingress' ? <Sun className="w-5 h-5" data-id="element-22405" /> : e.event_type === 'retrograde' ? <AlertCircle className="w-5 h-5" data-id="element-22406" /> : <Moon className="w-5 h-5" data-id="element-22407" />,
      color: e.event_type === 'eclipse' ? 'text-amber-500' : e.event_type === 'retrograde' ? 'text-rose-500' : e.event_type === 'ingress' ? 'text-emerald-500' : 'text-indigo-500',
      bg: e.event_type === 'eclipse' ? 'bg-amber-100' : e.event_type === 'retrograde' ? 'bg-rose-100' : e.event_type === 'ingress' ? 'bg-emerald-100' : 'bg-indigo-100',
      fullDesc: e.description_full,
      influence: e.influence
    }));
  }, []);
  // Filter events for current month
  const currentMonthEvents = useMemo(() => {
    return allEvents.filter(event => {
      const eventDate = new Date(event.dateStr);
      return eventDate.getMonth() === currentDate.getMonth() && eventDate.getFullYear() === currentDate.getFullYear();
    }).sort((a, b) => new Date(a.dateStr).getTime() - new Date(b.dateStr).getTime());
  }, [currentDate]);
  const calendarDays = useMemo(() => {
    return getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth());
  }, [currentDate]);
  const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  // Get current forecast based on selections
  const currentForecast = useMemo(() => {
    return initialForecasts.find(f => f.status === 'published' && f.forecast_type === forecastPeriod && (f.zodiac_sign === selectedSign || f.scope === 'general'));
  }, [selectedSign, forecastPeriod]);
  return <div className="w-full bg-slate-50 min-h-screen pb-24" data-id="element-22408">
      <div className="bg-white border-b border-slate-200 pt-6 pb-8" data-id="element-22409">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22410">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Астро-календарь'
        }]} className="mb-6" data-id="element-22411" />

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-22412">
            <div data-id="element-22413">
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" data-id="element-22414">
                Астрологический календарь
              </h1>
              <p className="text-slate-600" data-id="element-22415">
                События, прогнозы и лунные фазы из единой CMS
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-22416">
              {/* Moon void of course widget */}
              <div className="flex items-center flex-wrap bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium border border-indigo-100" data-id="element-22417">
                <Moon className="w-4 h-4 mr-1.5 sm:mr-2 text-indigo-500" data-id="element-22418" />
                <span data-id="element-22419">Луна без курса:</span>
                <span className="ml-1.5 sm:ml-2 font-bold" data-id="element-22420">14:56 - 18:30</span>
                <div className="group relative ml-2 cursor-help" data-id="element-22421">
                  <Info className="w-4 h-4 text-indigo-400" data-id="element-22422" />
                  <div className="absolute bottom-full right-0 sm:left-1/2 sm:transform sm:-translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-22423">
                    Период неэффективности. Не рекомендуется начинать новые
                    важные дела.
                  </div>
                </div>
              </div>

              <div className="flex bg-slate-100 p-1 rounded-xl w-fit" data-id="element-22424">
                <button onClick={() => setView('month')} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${view === 'month' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-22425">
                  <CalendarIcon className="w-4 h-4 mr-2" data-id="element-22426" /> Месяц
                </button>
                <button onClick={() => setView('list')} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${view === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-22427">
                  <List className="w-4 h-4 mr-2" data-id="element-22428" /> Список
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" data-id="element-22429">
        <div className="mt-8" data-id="element-22430">
          <Tabs tabs={[{
          id: 'calendar',
          label: 'Календарь событий'
        }, {
          id: 'horoscope',
          label: 'Гороскоп (Прогнозы)'
        }, {
          id: 'moon',
          label: 'Луна'
        }]} activeTab={mainTab} onChange={setMainTab} variant="underlined" className="border-b border-slate-200" data-id="element-22431" />
        </div>

        {mainTab === 'calendar' && <div className="flex flex-col lg:flex-row gap-8" data-id="element-22432">
            {/* Left Column - Calendar/List */}
            <div className="flex-1" data-id="element-22433">
              <div className="flex items-center justify-between mb-4 sm:mb-8" data-id="element-22434">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 capitalize" data-id="element-22435">
                  {formatMonthYear(currentDate)}
                </h2>
                <div className="flex space-x-2" data-id="element-22436">
                  <Button variant="outline" size="sm" icon={<ChevronLeft className="w-4 h-4" data-id="element-22438" />} onClick={prevMonth} data-id="element-22437" />
                  <Button variant="outline" size="sm" icon={<ChevronRight className="w-4 h-4" data-id="element-22440" />} onClick={nextMonth} data-id="element-22439" />
                </div>
              </div>

              {view === 'list' ? <div className="space-y-4" data-id="element-22441">
                  {currentMonthEvents.length > 0 ? currentMonthEvents.map(event => <Card key={event.id} hoverable padding="lg" className="flex flex-col sm:flex-row gap-6 cursor-pointer" onClick={() => onNavigate('calendar-event')} data-id="element-22442">
                        <div className="flex-shrink-0 text-center sm:text-left sm:w-32" data-id="element-22443">
                          <div className="text-sm text-slate-500 font-medium" data-id="element-22444">
                            {event.displayDate.split(' ')[1]}
                          </div>
                          <div className="text-3xl font-bold text-slate-900" data-id="element-22445">
                            {event.displayDate.split(' ')[0]}
                          </div>
                        </div>
                        <div className="flex-grow" data-id="element-22446">
                          <div className="flex items-center gap-3 mb-2" data-id="element-22447">
                            <div className={`p-1.5 rounded-lg ${event.bg} ${event.color}`} data-id="element-22448">
                              {event.icon}
                            </div>
                            <Badge variant="neutral" data-id="element-22449">{event.type}</Badge>
                          </div>
                          <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-22450">
                            {event.title}
                          </h3>
                          <p className="text-slate-600" data-id="element-22451">{event.desc}</p>
                        </div>
                        <div className="flex-shrink-0 flex items-center justify-center sm:justify-end" data-id="element-22452">
                          <Button variant="ghost" className="hidden sm:flex" onClick={e => {
                  e.stopPropagation();
                  onNavigate('calendar-event');
                }} data-id="element-22453">
                            Подробнее
                          </Button>
                        </div>
                      </Card>) : <Card padding="xl" className="text-center text-slate-500" data-id="element-22454">
                      <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" data-id="element-22455" />
                      <p className="text-lg font-medium text-slate-900 mb-2" data-id="element-22456">
                        Нет важных событий
                      </p>
                      <p data-id="element-22457">В этом месяце нет значимых астрологических событий.</p>
                    </Card>}
                </div> : <Card padding="none" className="overflow-hidden overflow-y-visible" onClick={() => setActiveTooltipDay(null)} data-id="element-22458">
                  <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200" data-id="element-22459">
                    {weekDays.map((day, i) => <div key={i} className="py-2 sm:py-3 text-center text-[11px] sm:text-sm font-semibold text-slate-600" data-id="element-22460">
                        {day}
                      </div>)}
                  </div>
                  <div className="grid grid-cols-7 auto-rows-[56px] sm:auto-rows-[100px] md:auto-rows-[120px] divide-x divide-y divide-slate-100" data-id="element-22461">
                    {calendarDays.map((date, i) => {
                if (!date) {
                  return <div key={`empty-${i}`} className="bg-slate-50/50" data-id="element-22462"></div>;
                }
                const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                const dayEvents = currentMonthEvents.filter(e => e.dateStr === dateStr);
                const isToday = new Date().toDateString() === date.toDateString();
                return <div key={i} className={`group/day p-1 sm:p-2 relative transition-colors ${dayEvents.length > 0 ? 'hover:bg-slate-50 cursor-pointer' : ''} ${isToday ? 'bg-indigo-50/30' : ''}`} onClick={e => {
                  if (dayEvents.length > 0) {
                    e.stopPropagation();
                    setActiveTooltipDay(activeTooltipDay === i ? null : i);
                  }
                }} data-id="element-22463">
                          <div className={`text-xs sm:text-sm font-medium w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full mb-0.5 sm:mb-1 ${isToday ? 'bg-indigo-600 text-white' : 'text-slate-700'}`} data-id="element-22464">
                            {date.getDate()}
                          </div>

                          {/* Mobile: colored dots only */}
                          <div className="flex gap-0.5 flex-wrap sm:hidden" data-id="element-22465">
                            {dayEvents.map(event => <div key={event.id} className={`w-1.5 h-1.5 rounded-full ${event.bg.replace('100', '400')}`} data-id="element-22466" />)}
                          </div>

                          {/* Desktop: event labels */}
                          <div className="hidden sm:block space-y-1 overflow-y-auto hide-scrollbar max-h-[60px] md:max-h-[80px]" data-id="element-22467">
                            {dayEvents.map(event => <div key={event.id} className={`text-[10px] md:text-xs px-1.5 py-0.5 md:py-1 rounded truncate ${event.bg} ${event.color} font-medium`} title={event.title} data-id="element-22468">
                                {event.title}
                              </div>)}
                          </div>

                          {/* Tap/hover tooltip for days with events */}
                          {dayEvents.length > 0 && activeTooltipDay === i && <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-30 animate-in fade-in duration-150" onClick={e => e.stopPropagation()} data-id="element-22469">
                              <div className="bg-slate-900 text-white text-[11px] sm:text-xs rounded-xl shadow-xl px-3 py-2 min-w-[140px] max-w-[200px]" data-id="element-22470">
                                {dayEvents.map((event, idx) => <div key={event.id} className={`${idx > 0 ? 'mt-1.5 pt-1.5 border-t border-slate-700' : ''}`} data-id="element-22471">
                                    <div className="font-medium truncate" data-id="element-22472">
                                      {event.title}
                                    </div>
                                    <div className="text-slate-400 text-[10px] mt-0.5" data-id="element-22473">
                                      {event.type}
                                    </div>
                                  </div>)}
                                <button onClick={e => {
                        e.stopPropagation();
                        setActiveTooltipDay(null);
                        onNavigate('calendar-event');
                      }} className="mt-2 pt-1.5 border-t border-slate-700 text-indigo-300 hover:text-indigo-200 font-medium text-[11px] flex items-center gap-1 w-full transition-colors" data-id="element-22474">
                                  Подробнее <ArrowRight className="w-3 h-3" data-id="element-22475" />
                                </button>
                                <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-slate-900" data-id="element-22476" />
                              </div>
                            </div>}
                        </div>;
              })}
                  </div>
                </Card>}
            </div>

            {/* Right Column - Current Transits & Forecast */}
            <div className="w-full lg:w-1/3" data-id="element-22477">
              {/* Moon Void-of-Course Widget + Current Transits */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-10" data-id="element-22478">
                {/* Moon Status */}
                <Card padding="md" className="border-amber-200 bg-amber-50/50 lg:col-span-1 self-start" data-id="element-22479">
                  <div className="flex items-start gap-3" data-id="element-22480">
                    <div className="p-2 rounded-xl bg-amber-100 text-amber-600" data-id="element-22481">
                      <Moon className="w-5 h-5" data-id="element-22482" />
                    </div>
                    <div data-id="element-22483">
                      <div className="font-bold text-slate-900 mb-1" data-id="element-22484">
                        Луна без курса
                      </div>
                      <div className="text-sm text-slate-700 mb-2" data-id="element-22485">
                        ☽ Луна в Водолее • до{' '}
                        <span className="font-semibold" data-id="element-22486">14:56 МСК</span>
                      </div>
                      <div className="flex items-start gap-1.5 text-xs text-slate-500" data-id="element-22487">
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-22488" />
                        <span data-id="element-22489">
                          Не рекомендуется начинать важные дела, подписывать
                          документы и делать крупные покупки.
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Current Transits */}
                <Card padding="none" className="lg:col-span-2 overflow-hidden" data-id="element-22490">
                  <div className="px-4 py-3 bg-slate-50/80 border-b border-slate-100 flex items-center justify-between" data-id="element-22491">
                    <div className="font-bold text-slate-900 text-sm" data-id="element-22492">
                      Текущие транзиты
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-22493">
                      Сегодня, 22:46 МСК
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100" data-id="element-22494">
                    {currentTransits.map((t, i) => <div key={i} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50/50 transition-colors" data-id="element-22495">
                        <span className="text-xs sm:text-sm text-slate-500 w-24 sm:w-28 flex-shrink-0" data-id="element-22496">
                          {t.planet}
                        </span>
                        <span className={`font-bold text-xs sm:text-sm ${elementColors[t.element]} flex-1 text-center`} data-id="element-22497">
                          {t.sign}
                        </span>
                        <span className="text-[10px] sm:text-xs text-slate-400 font-mono w-14 sm:w-16 text-right" data-id="element-22498">
                          {t.degree}
                        </span>
                      </div>)}
                  </div>
                </Card>
              </div>

              {/* Forecast Section */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-id="element-22499">
                <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-22500">
                  Гороскоп на сегодня
                </h3>
                <div className="flex flex-col md:flex-row gap-4 mb-4" data-id="element-22501">
                  <div className="flex-1" data-id="element-22502">
                    <div className="flex items-center justify-between mb-2" data-id="element-22503">
                      <span className="text-sm font-medium text-slate-600" data-id="element-22504">
                        Знак зодиака
                      </span>
                      <select value={selectedSign} onChange={e => setSelectedSign(e.target.value)} className="text-sm border border-slate-200 rounded px-2 py-1 bg-white" data-id="element-22505">
                        {ZODIAC_SIGNS.map(sign => <option key={sign.key} value={sign.key} data-id="element-22506">
                            {sign.ru}
                          </option>)}
                      </select>
                    </div>
                    <div className="flex items-center justify-between mb-2" data-id="element-22507">
                      <span className="text-sm font-medium text-slate-600" data-id="element-22508">
                        Период
                      </span>
                      <select value={forecastPeriod} onChange={e => setForecastPeriod(e.target.value)} className="text-sm border border-slate-200 rounded px-2 py-1 bg-white" data-id="element-22509">
                        <option value="day" data-id="element-22510">Сегодня</option>
                        <option value="week" data-id="element-22511">Неделя</option>
                        <option value="month" data-id="element-22512">Месяц</option>
                      </select>
                    </div>
                  </div>
                </div>

                {currentForecast ? <div className="space-y-4" data-id="element-22513">
                    <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg" data-id="element-22514">
                      <div className="flex items-center gap-2" data-id="element-22515">
                        <Calendar className="w-4 h-4 text-slate-600" data-id="element-22516" />
                        <span className="text-sm font-medium text-slate-900" data-id="element-22517">
                          {formatDateRu(currentForecast.date_start)}
                          {currentForecast.date_start !== currentForecast.date_end && ` — ${formatDateRu(currentForecast.date_end)}`}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500" data-id="element-22518">
                        {currentForecast.title}
                      </span>
                    </div>

                    <div className="prose prose-slate max-w-none" data-id="element-22519">
                      <p className="text-lg text-slate-700 leading-relaxed" data-id="element-22520">
                        {currentForecast.content.general}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-22521">
                      <div className="bg-rose-50 p-4 rounded-lg border border-rose-100" data-id="element-22522">
                        <div className="flex items-center gap-2 mb-2" data-id="element-22523">
                          <Star className="w-4 h-4 text-rose-600" data-id="element-22524" />
                          <span className="text-sm font-bold text-rose-900" data-id="element-22525">
                            Любовь
                          </span>
                        </div>
                        <p className="text-sm text-rose-800" data-id="element-22526">
                          {currentForecast.content.love_text}
                        </p>
                      </div>
                      <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-100" data-id="element-22527">
                        <div className="flex items-center gap-2 mb-2" data-id="element-22528">
                          <Star className="w-4 h-4 text-emerald-600" data-id="element-22529" />
                          <span className="text-sm font-bold text-emerald-900" data-id="element-22530">
                            Карьера
                          </span>
                        </div>
                        <p className="text-sm text-emerald-800" data-id="element-22531">
                          {currentForecast.content.career_text}
                        </p>
                      </div>
                      <div className="bg-sky-50 p-4 rounded-lg border border-sky-100" data-id="element-22532">
                        <div className="flex items-center gap-2 mb-2" data-id="element-22533">
                          <Star className="w-4 h-4 text-sky-600" data-id="element-22534" />
                          <span className="text-sm font-bold text-sky-900" data-id="element-22535">
                            Здоровье
                          </span>
                        </div>
                        <p className="text-sm text-sky-800" data-id="element-22536">
                          {currentForecast.content.health_text}
                        </p>
                      </div>
                    </div>

                    <div className="bg-slate-900 text-white p-4 rounded-lg border border-slate-200" data-id="element-22537">
                      <h4 className="font-bold text-lg mb-2 flex items-center gap-2" data-id="element-22538">
                        <Star className="w-5 h-5 text-amber-400" data-id="element-22539" /> Энергия
                        периода
                      </h4>
                      <div className="space-y-2" data-id="element-22540">
                        <div className="flex justify-between items-center border-b border-white/10 pb-2" data-id="element-22541">
                          <span className="text-slate-400" data-id="element-22542">Настроение</span>
                          <span className="font-medium" data-id="element-22543">
                            {currentForecast.content.mood}
                          </span>
                        </div>
                        <div className="flex justify-between items-center border-b border-white/10 pb-2" data-id="element-22544">
                          <span className="text-slate-400" data-id="element-22545">Цвет удачи</span>
                          <span className="font-medium" data-id="element-22546">
                            {currentForecast.content.lucky_color}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pb-2" data-id="element-22547">
                          <span className="text-slate-400" data-id="element-22548">Число удачи</span>
                          <span className="font-medium" data-id="element-22549">
                            {currentForecast.content.lucky_number}
                          </span>
                        </div>
                      </div>
                    </div>

                    {currentForecast.content.recommendation && <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100" data-id="element-22550">
                        <h4 className="font-bold text-indigo-900 mb-2" data-id="element-22551">
                          Совет астролога
                        </h4>
                        <p className="text-sm text-indigo-800 mb-4 italic" data-id="element-22552">
                          "{currentForecast.content.recommendation}"
                        </p>
                        {currentForecast.content.cta_text && <Button variant="primary" className="w-full" onClick={() => onNavigate('services')} data-id="element-22553">
                            {currentForecast.content.cta_text}
                          </Button>}
                      </div>}
                  </div> : <Card padding="xl" className="text-center py-20" data-id="element-22554">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-22555">
                      <AlertCircle className="w-8 h-8 text-slate-400" data-id="element-22556" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-22557">
                      Прогноз готовится
                    </h3>
                    <p className="text-slate-500 max-w-md mx-auto" data-id="element-22558">
                      Наши астрологи (или AI) еще не опубликовали прогноз для
                      этого знака на выбранный период. Загляните позже!
                    </p>
                  </Card>}
              </div>
            </div>
          </div>}

        {mainTab === 'horoscope' && <div className="space-y-8" data-id="element-22559">
            <div className="flex flex-col md:flex-row gap-6 justify-between items-start md:items-center bg-white p-4 rounded-2xl border border-slate-200 shadow-sm" data-id="element-22560">
              <div className="w-full md:w-auto overflow-x-auto pb-2 md:pb-0 scrollbar-hide" data-id="element-22561">
                <div className="flex gap-2 min-w-max" data-id="element-22562">
                  {ZODIAC_SIGNS.map(sign => <button key={sign.key} onClick={() => setSelectedSign(sign.key)} className={`flex flex-col items-center justify-center p-3 rounded-xl transition-all min-w-[80px] ${selectedSign === sign.key ? 'bg-indigo-600 text-white shadow-md transform scale-105' : 'bg-slate-50 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600'}`} data-id="element-22563">
                      <span className="text-2xl mb-1" data-id="element-22564">{sign.symbol}</span>
                      <span className="text-xs font-medium" data-id="element-22565">{sign.ru}</span>
                    </button>)}
                </div>
              </div>

              <div className="flex bg-slate-100 p-1 rounded-lg w-full md:w-auto" data-id="element-22566">
                <button className={`flex-1 md:flex-none px-4 py-2 text-sm font-medium rounded-md transition-colors ${forecastPeriod === 'day' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => setForecastPeriod('day')} data-id="element-22567">
                  Сегодня
                </button>
                <button className={`flex-1 md:flex-none px-4 py-2 text-sm font-medium rounded-md transition-colors ${forecastPeriod === 'week' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => setForecastPeriod('week')} data-id="element-22568">
                  Неделя
                </button>
                <button className={`flex-1 md:flex-none px-4 py-2 text-sm font-medium rounded-md transition-colors ${forecastPeriod === 'month' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => setForecastPeriod('month')} data-id="element-22569">
                  Месяц
                </button>
              </div>
            </div>

            {currentForecast ? <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-22570">
                <div className="lg:col-span-2 space-y-6" data-id="element-22571">
                  <Card padding="lg" className="border-indigo-100 shadow-sm" data-id="element-22572">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-22573">
                      {currentForecast.title}
                    </h2>
                    <p className="text-sm text-slate-500 mb-6 flex items-center gap-2" data-id="element-22574">
                      <Calendar className="w-4 h-4" data-id="element-22575" />
                      {formatDateRu(currentForecast.date_start)}
                      {currentForecast.date_start !== currentForecast.date_end && ` — ${formatDateRu(currentForecast.date_end)}`}
                    </p>

                    <div className="prose prose-slate max-w-none" data-id="element-22576">
                      <p className="text-lg text-slate-700 leading-relaxed" data-id="element-22577">
                        {currentForecast.content.general}
                      </p>
                    </div>
                  </Card>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-22578">
                    <Card padding="lg" className="bg-rose-50 border-rose-100" data-id="element-22579">
                      <div className="flex items-center justify-between mb-3" data-id="element-22580">
                        <h3 className="font-bold text-rose-900" data-id="element-22581">Любовь</h3>
                        <span className="text-sm font-bold text-rose-600" data-id="element-22582">
                          {currentForecast.content.love_percent}%
                        </span>
                      </div>
                      <p className="text-sm text-rose-800" data-id="element-22583">
                        {currentForecast.content.love_text}
                      </p>
                    </Card>
                    <Card padding="lg" className="bg-emerald-50 border-emerald-100" data-id="element-22584">
                      <div className="flex items-center justify-between mb-3" data-id="element-22585">
                        <h3 className="font-bold text-emerald-900" data-id="element-22586">Карьера</h3>
                        <span className="text-sm font-bold text-emerald-600" data-id="element-22587">
                          {currentForecast.content.career_percent}%
                        </span>
                      </div>
                      <p className="text-sm text-emerald-800" data-id="element-22588">
                        {currentForecast.content.career_text}
                      </p>
                    </Card>
                    <Card padding="lg" className="bg-sky-50 border-sky-100" data-id="element-22589">
                      <div className="flex items-center justify-between mb-3" data-id="element-22590">
                        <h3 className="font-bold text-sky-900" data-id="element-22591">Здоровье</h3>
                        <span className="text-sm font-bold text-sky-600" data-id="element-22592">
                          {currentForecast.content.health_percent}%
                        </span>
                      </div>
                      <p className="text-sm text-sky-800" data-id="element-22593">
                        {currentForecast.content.health_text}
                      </p>
                    </Card>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-22594">
                  <Card padding="lg" className="bg-slate-900 text-white border-none" data-id="element-22595">
                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2" data-id="element-22596">
                      <Star className="w-5 h-5 text-amber-400" data-id="element-22597" /> Энергия
                      периода
                    </h3>
                    <div className="space-y-4" data-id="element-22598">
                      <div className="flex justify-between items-center border-b border-white/10 pb-2" data-id="element-22599">
                        <span className="text-slate-400" data-id="element-22600">Настроение</span>
                        <span className="font-medium" data-id="element-22601">
                          {currentForecast.content.mood}
                        </span>
                      </div>
                      <div className="flex justify-between items-center border-b border-white/10 pb-2" data-id="element-22602">
                        <span className="text-slate-400" data-id="element-22603">Цвет удачи</span>
                        <span className="font-medium" data-id="element-22604">
                          {currentForecast.content.lucky_color}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pb-2" data-id="element-22605">
                        <span className="text-slate-400" data-id="element-22606">Число удачи</span>
                        <span className="font-medium" data-id="element-22607">
                          {currentForecast.content.lucky_number}
                        </span>
                      </div>
                    </div>
                  </Card>

                  <Card padding="lg" className="border-indigo-100 bg-indigo-50/50" data-id="element-22608">
                    <h3 className="font-bold text-indigo-900 mb-2" data-id="element-22609">
                      Совет астролога
                    </h3>
                    <p className="text-sm text-indigo-800 mb-6 italic" data-id="element-22610">
                      "{currentForecast.content.recommendation}"
                    </p>

                    {currentForecast.content.cta_text && <Button variant="primary" className="w-full" onClick={() => onNavigate('services')} data-id="element-22611">
                        {currentForecast.content.cta_text}
                      </Button>}
                  </Card>
                </div>
              </div> : <Card padding="xl" className="text-center py-20" data-id="element-22612">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-22613">
                  <AlertCircle className="w-8 h-8 text-slate-400" data-id="element-22614" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-22615">
                  Прогноз готовится
                </h3>
                <p className="text-slate-500 max-w-md mx-auto" data-id="element-22616">
                  Наши астрологи (или AI) еще не опубликовали прогноз для этого
                  знака на выбранный период. Загляните позже!
                </p>
              </Card>}
          </div>}

        {mainTab === 'moon' && <div className="text-center py-20" data-id="element-22617">
            <Moon className="w-16 h-16 text-slate-300 mx-auto mb-4" data-id="element-22618" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-22619">
              Лунный календарь
            </h2>
            <p className="text-slate-500" data-id="element-22620">
              Раздел находится в разработке. Скоро здесь появится подробная
              информация о лунных фазах и днях.
            </p>
          </div>}
      </div>

      {/* Cross-links */}
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-22621">
        <button onClick={() => onNavigate('daily-horoscope')} className="flex items-center gap-4 p-5 bg-white rounded-2xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all text-left group" data-id="element-22622">
          <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-2xl flex-shrink-0 group-hover:bg-amber-100 transition-colors" data-id="element-22623">
            ☀️
          </div>
          <div data-id="element-22624">
            <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-22625">
              Гороскоп на сегодня
            </div>
            <div className="text-sm text-slate-500" data-id="element-22626">
              Персональный прогноз для вашего знака
            </div>
          </div>
        </button>
        <button onClick={() => onNavigate('moon-calendar')} className="flex items-center gap-4 p-5 bg-white rounded-2xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all text-left group" data-id="element-22627">
          <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-2xl flex-shrink-0 group-hover:bg-indigo-100 transition-colors" data-id="element-22628">
            🌙
          </div>
          <div data-id="element-22629">
            <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-22630">
              Лунный календарь
            </div>
            <div className="text-sm text-slate-500" data-id="element-22631">
              Фазы луны и рекомендации на день
            </div>
          </div>
        </button>
      </div>

      {/* CTA Section */}
      <div className="mt-16 bg-gradient-to-br from-slate-900 to-indigo-950 rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden shadow-xl" data-id="element-22632">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-22633" />
        <div className="relative z-10" data-id="element-22634">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" data-id="element-22635">
            Как эти события повлияют на вас?
          </h2>
          <p className="text-indigo-200 mb-8 max-w-xl mx-auto" data-id="element-22636">
            Узнайте, какие сферы вашей жизни затронут предстоящие затмения и
            ретрограды. Закажите персональный прогноз транзитов.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-22637">
            <button onClick={() => onNavigate('service-forecast')} className="px-8 py-3 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-colors" data-id="element-22638">
              Заказать прогноз транзитов
            </button>
            <button onClick={() => onNavigate('astrologers')} className="px-8 py-3 border border-white/20 text-white rounded-xl font-semibold hover:bg-white/10 transition-colors" data-id="element-22639">
              Консультация астролога
            </button>
          </div>
        </div>
      </div>
    </div>;
}